import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Envelope, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10] flex flex-col">
      <Topbar />
      <div className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          <div className="label-tag">// Password recovery</div>
          <h1 className="font-display text-3xl font-black mt-2">Forgot your password?</h1>
          <p className="text-sm text-gray-400 mt-2">
            Enter the email tied to your EEngDeS account. We&apos;ll send you a one-time
            link to set a new password.
          </p>

          {sent ? (
            <div className="mt-8 p-5 border border-[#00E5FF]/40 bg-[#00E5FF]/5 font-mono text-sm text-cyan-100 space-y-2" data-testid="forgot-password-success">
              <div className="inline-flex items-center gap-2 text-[#00E5FF] font-bold">
                <CheckCircle size={16} weight="fill" /> Check your inbox
              </div>
              <p className="text-gray-300 text-xs leading-relaxed">
                If an account exists for <span className="text-[#00E5FF]">{email}</span>,
                we just sent a reset link valid for 60 minutes. Look in spam too — sometimes
                transactional mail gets snagged.
              </p>
              <Link to="/login" className="block mt-3 text-xs text-gray-400 hover:text-white">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6" data-testid="forgot-password-form">
              {error && (
                <div className="mb-4 p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono" data-testid="forgot-password-error">
                  {error}
                </div>
              )}
              <label className="block">
                <span className="label-tag">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full bg-black/40 border border-white/10 px-3 py-3 text-white font-mono text-sm focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                  placeholder="you@example.com"
                  data-testid="forgot-password-email"
                />
              </label>
              <button
                type="submit"
                disabled={busy || !email}
                className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
                data-testid="forgot-password-submit"
              >
                {busy ? "Sending…" : (<><Envelope size={18} weight="bold" /> Send reset link <ArrowRight size={16} /></>)}
              </button>
              <p className="mt-6 text-sm text-gray-400">
                Remembered it? <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="link-back-login">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
